import { sumCents } from '../../shared/money/cents.js'
import type { Cents } from '../../shared/money/cents.js'
import type { Merchant } from '../product/merchant.js'
import type { Transaction } from './transaction.js'
import { merchantKeyOf } from './merchant-key.js'
import { liquidoDe, precisaDeCategoria } from './abatimento.js'

/**
 * Um estabelecimento e tudo que ainda não foi classificado nele.
 *
 * A unidade de decisão da tela de classificação: dizer que "uber" é Transporte
 * resolve 111 transações de uma vez.
 */
export type MerchantGroup = {
  /** A chave normalizada. É ela que o aprendizado guarda. */
  key: string
  /**
   * A descrição **crua** mais frequente do grupo. É o que a pessoa reconhece —
   * `uber` não diz de onde veio, `Uber Uber *Trip Help.U` diz.
   */
  label: string
  /** Ordenados como vieram; a tela mostra os primeiros como amostra. */
  transactionIds: string[]
  count: number
  /** Soma das saídas, **positiva**. O sinal já disse o que precisava dizer. */
  totalCents: Cents
}

/**
 * Os grupos a classificar, do que mais pesa para o que menos pesa.
 *
 * ## Por valor, e não por quantidade
 *
 * Medido nesta casa: as **50 maiores chaves cobrem 88% do valor** que sobra
 * depois das exclusões abaixo — 552 grupos, 2158 transações, R$ 865.735. Ordenar
 * por quantidade poria "compra de criptomoedas" (137 transações) acima de
 * "lake boulevard" (9 transações, R$ 160 mil) — e a segunda é a que muda o
 * relatório.
 *
 * ## O que fica de fora, e por que cada exclusão importa
 *
 * - **`settlement`** — pagamento de fatura. As compras do cartão já entraram
 *   como `deferred`; classificar o pagamento contaria o mesmo dinheiro duas
 *   vezes. Medido: era o **segundo maior grupo por valor**, R$ 155 mil, e
 *   teria virado uma categoria inteira de gasto que não existe.
 * - **`internal`** — dinheiro andando entre contas da própria casa. Não é
 *   gasto por definição.
 * - **`canceled`** — `transaction.ts` diz que sai de todas as somas.
 * - **Entradas.** A tela classifica gasto; salário e resgate são outra
 *   conversa, e misturá-los faria a ordenação por valor começar pelo que
 *   entrou.
 */
export function groupForClassification(transactions: Transaction[]): MerchantGroup[] {
  const porChave = new Map<string, Transaction[]>()

  for (const t of transactions) {
    if (t.status === 'canceled') continue
    if (t.cashImpact === 'settlement' || t.cashImpact === 'internal') continue
    if (t.source.amountCents >= 0) continue
    // A entrada ligada segue o gasto, e a compra zerada não pede nada — ver
    // `precisaDeCategoria`.
    if (!precisaDeCategoria(t)) continue

    const chave = chaveDe(t.source.description)
    if (chave === '') continue

    porChave.set(chave, [...(porChave.get(chave) ?? []), t])
  }

  return [...porChave.entries()]
    .map(([key, lista]) => ({
      key,
      label: descricaoMaisFrequente(lista),
      transactionIds: lista.map((t) => t.id),
      count: lista.length,
      // O líquido, e não o bruto: da compra abatida, só o que sobrou é gasto
      // da casa e vai receber a categoria (`abatimento.ts`).
      totalCents: sumCents(lista.map((t) => Math.abs(liquidoDe(t)))),
    }))
    .sort((a, b) => b.totalCents - a.totalCents || a.label.localeCompare(b.label, 'pt-BR'))
}

/**
 * A chave, com a descrição crua como último recurso.
 *
 * `merchantKeyOf` devolve vazio quando a descrição é só número. Cair fora
 * levaria a transação a sumir da tela sem explicação — e uma transação que não
 * aparece em lugar nenhum é a que nunca é classificada. Com a descrição crua,
 * ela vira um grupo de uma, visível e decidível.
 */
function chaveDe(description: string): string {
  const chave = merchantKeyOf(description)
  return chave === '' ? description.trim().toLowerCase() : chave
}

/**
 * A grafia mais comum do grupo — e, no empate, a mais curta.
 *
 * O empate é o caso normal quando o grupo tem uma transação de cada grafia, e
 * sem regra de desempate o rótulo mudaria de lugar a cada carregamento, pela
 * ordem em que o Firestore devolveu. A mais curta costuma ser a mais limpa:
 * entre `Uber` e `Uber Uber *Trip Help.U`, "Uber".
 */
function descricaoMaisFrequente(lista: Transaction[]): string {
  const contagem = new Map<string, number>()
  for (const t of lista) {
    const d = semRotuloDoBanco(t.source.description)
    if (d !== '') contagem.set(d, (contagem.get(d) ?? 0) + 1)
  }
  let melhor = ''
  let melhorN = 0
  for (const [d, n] of contagem) {
    if (n > melhorN || (n === melhorN && d.length < melhor.length)) {
      melhor = d
      melhorN = n
    }
  }
  return melhor
}

/**
 * A categoria que esta chave já ganhou antes, ou `null`.
 *
 * É o que faz a classificação não precisar ser refeita todo mês: decidido uma
 * vez que `uber` é Transporte, o Uber do mês que vem chega sugerido.
 *
 * **Casa por `patterns`, e não por nome.** `Uber Uber *Trip Help.U` vira `uber`
 * e nunca seria digitado por ninguém; o nome existe para a loja que a pessoa
 * cadastrou à mão num registro de preço, e comparar por ele aqui casaria por
 * acaso.
 *
 * Loja sem `defaultCategoryId` devolve `null` — conhecer o padrão e não ter
 * opinião sobre a categoria é um estado normal, e não um erro.
 */
export function suggestedCategoryFor(key: string, merchants: Merchant[]): string | null {
  if (key === '') return null
  return merchants.find((m) => m.patterns.includes(key))?.defaultCategoryId ?? null
}

/**
 * `'Transferência enviada|MINISTERIO DA FAZENDA'` → `'MINISTERIO DA FAZENDA'`.
 *
 * O rótulo do banco antes da barra é o mesmo em centenas de linhas, e o que
 * identifica está depois. Na tela estreita o prefixo comia a coluna inteira e o
 * nome saía cortado — `'Pagamento efetuado|LA…'` —, escondendo exatamente o que
 * a pessoa precisa para decidir a categoria. Visto em produção.
 *
 * É a mesma regra que `merchantKeyOf` já aplicava para agrupar; aqui ela chega
 * ao que se mostra. Agrupar por uma coisa e rotular por outra era a origem do
 * defeito.
 */
function semRotuloDoBanco(description: string): string {
  const barra = description.indexOf('|')
  return (barra === -1 ? description : description.slice(barra + 1)).trim()
}
